import { useMemo, useState } from 'react';

import { addNarrativeRequest } from 'utilitys/narrative/narrativeTools';
import { WidgetViewMode, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useProductivityRateStore } from 'store/productivityRate.store';

import { useMutationExportToCssvRunRateAnalysis } from 'api/mutations/exportToCssvRunRateAnalysis.mutation';
import { useQueryGetProjectDatesForResourceChart } from 'api/queries/getProjectDatesForResourceChart.query';
import { useQueryProductivityRate } from 'api/queries/productivityRate.query';

import { getInputLabel } from 'components/common/CustomFilters/TableDateRangeFilter/TableDateRangeFilter.utils';
import { FilterNames, getFiltersData } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';
import { rangeOptions } from 'components/common/GlobalFilterComponent/components/RangeFilter/rangeFilter';

import { usePinboardWidgetWrapperContext } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';
import { aggregationOptions } from 'components/widgets/RunRateChart/productivityRate.constants';

export type UseProductivityRateProvider = ReturnType<typeof useProductivityRateProvider>;

interface Props {
    componentKey: string;
    widgetId?: string;
    latestVersionId?: number | undefined;
    compareVersionId?: number | undefined;
    globalFilterData: any;
    localFilters: any;
    widgetViewMode?: WidgetViewMode;
}

export const useProductivityRateProvider = ({
    widgetId,
    latestVersionId,
    localFilters,
    globalFilterData,
    compareVersionId,
    componentKey,
    widgetViewMode,
}: Props) => {
    const { isGenerativeAi, reportId } = usePinboardWidgetWrapperContext();
    const isInPinboard = Boolean(widgetId);

    const {
        aggregation: aggregationFromStore,
        update: updateProductivityRateStore,
        dataPointIndex: dataPointIndexFromStore,
        seriesIndex: seriesIndexFromStore,
    } = useProductivityRateStore();

    const [aggregation, setAggregation] = useState(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? localFilters?.aggregation : aggregationFromStore,
    );
    const onSetAggregation = (value) => {
        setAggregation(value);
        updateProductivityRateStore({ aggregation: value });
    };

    const source = isInPinboard
        ? localFilters
        : { ...globalFilterData, aggregation, dataPointIndexFromStore, seriesIndexFromStore };

    const getProjectDatesForResourceChart = useQueryGetProjectDatesForResourceChart({
        lastMetaDataId: latestVersionId,
        comparedMetaDataId: compareVersionId,
    });

    const { startDate, endDate } = useMemo(() => {
        const value = source?.dateRangeFilter;
        if (value && getProjectDatesForResourceChart.data) {
            if (Array.isArray(value)) {
                if (value[0] && value[1]) {
                    return {
                        startDate: value[0],
                        endDate: value[1],
                    };
                } else {
                    return {
                        startDate: getProjectDatesForResourceChart.data?.startProjectDate,
                        endDate: getProjectDatesForResourceChart.data?.endProjectDate,
                    };
                }
            }
        }
        return {
            startDate: null,
            endDate: null,
        };
    }, [source.dateRangeFilter, getProjectDatesForResourceChart.data]);

    const filters = useMemo(() => {
        return getFiltersData({
            [FilterNames.ACTIVITY_CODES]: source?.activityCodes
                ?.filter((item) => !item.value.isExclude)
                .map((i) => i.value.name),

            [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                ?.filter((item) => item.value.isExclude)
                .map((i) => i.value.name),

            [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,

            [FilterNames.TAG_CARD_BOARD]: source?.tags
                ?.filter((item) => !item.value.isExclude)
                .map((i) => i.value.name),

            [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags
                ?.filter((item) => item.value.isExclude)
                .map((i) => i.value.name),

            [FilterNames.WBS_SEARCH]: source?.wbs,

            [FilterNames.PRODUCTIVITY_PRE_DEFINED]: source?.productivityPreDefined,

            [FilterNames.ACTIVITY_TYPE_FILTER]: source?.activityType,
        });
    }, [
        source?.activityCodes,
        source?.milestoneFragnet,
        source?.tags,
        source?.wbs,
        source?.productivityPreDefined,
        source?.activityType,
    ]);

    const range = useMemo(() => {
        const value = source.dateRangeFilter;
        return Array.isArray(value) ? 'R_CUSTOM' : value;
    }, [source.dateRangeFilter]);

    const productivityRatePayload = {
        actualMetaDataId: latestVersionId,
        comparedMetaDataId: compareVersionId,
        range: range,
        startDate: startDate,
        endDate: endDate,
        aggregation: source.aggregation,
        filter: {
            filters: filters,
        },
        ...addNarrativeRequest(isGenerativeAi, componentKey, widgetId, reportId),
    };

    const getProductivityRateData = useQueryProductivityRate(productivityRatePayload);

    const rangeFilterValue = useMemo(() => {
        if (!range) return null;
        if (range === 'R_CUSTOM') {
            return getInputLabel({ startDate: startDate, endDate: endDate });
        }
        return rangeOptions.find((item) => item.value === range)?.label;
    }, [range, startDate, endDate]);

    // create pill data for PinboardWidgetPills component
    const pillsData = {
        activityCodes: source?.activityCodes?.filter((item) => !item.value?.isExclude).map((i) => i.value?.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.value?.isExclude).map((i) => i.value?.name),
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags?.filter((item) => !item.value?.isExclude).map((i) => i.value?.name),
        tagsExclude: source?.tags?.filter((item) => item.value?.isExclude).map((i) => i.value?.name),
        wbs: source?.wbs,
        productivityPreDefined: source?.productivityPreDefined,
        activityType: source?.activityType,
        dateRangeFilter: rangeFilterValue,
        aggregation: aggregationOptions.find((item) => item.value === source?.aggregation)?.label,
    };

    const { pillsList: pills } = useGlobalFiltersHook({ widgetId, source: pillsData, latestVersionId });

    return {
        ui: {
            state: {
                aggregation: source.aggregation,
                dateRangeFilter: source?.dateRangeFilter,
                dataPointIndexFromStore: source?.dataPointIndexFromStore,
                seriesIndexFromStore: source?.seriesIndexFromStore,
                activityCodes: source?.activityCodes,
                milestoneFragnet: source?.milestoneFragnet,
                tags: source?.tags,
                wbs: source?.wbs,
                productivityPreDefined: source?.productivityPreDefined,
                activityType: source?.activityType,
            },
            setters: {
                updateProductivityRateStore,
                onSetAggregation,
            },
            pills,
            isInPinboard,
        },
        api: {
            exportToCsvRunRateAnalysis: {
                instance: useMutationExportToCssvRunRateAnalysis(),
                payload: {
                    actualMetaDataId: latestVersionId,
                    comparedMetaDataId: compareVersionId,
                    range: range,
                    startDate: startDate,
                    endDate: endDate,
                    aggregation: source.aggregation,
                    filter: {
                        filters: filters,
                    },
                },
            },
            getProductivityRateData: {
                instance: getProductivityRateData,
                payload: productivityRatePayload,
            },
        },
    };
};
